import {
  ArrowLeft,
  Bot,
  Braces,
  Check,
  ChevronRight,
  GitBranch,
  Hand,
  Languages,
  ListTree,
  Loader2,
  Maximize2,
  MessageSquareText,
  MousePointer2,
  Phone,
  Plus,
  RotateCcw,
  Send,
  Settings2,
  Trash2,
  Wrench,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type WheelEvent as ReactWheelEvent,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useDiscardWarning } from "../../hooks/use-discard-warning";
import { core } from "../../lib/api";
import { normalizeInfieldLanguageOptions } from "../../lib/infieldLanguages";
import { getPromptMentionOptions } from "../../lib/promptVariables";
import { STT_LANGUAGE_GROUPS } from "../../lib/sttLanguages";
import { cn } from "../../lib/utils";
import { useToast } from "../../components/ui/toast";
import { OutcomeFindingsPanel } from "../agents/outcome-findings";
import { useOutcomeGate } from "../agents/outcome-gate";
import { findingsFromError, type OutcomeOverride } from "../agents/outcome-validation";
import {
  PostCallFieldEditor,
  PromptTokenMenu,
  SentenceLibrary,
  type PromptTokenOption,
  type StudioCallField,
  type StudioSentence,
} from "../agents/studio-editors";
import { TestCallModal } from "../home/test-call-modal";
import type { Agent } from "../home/types";
import {
  PublishVersionModal,
  type PublishMode,
} from "../agents/publish-version-modal";
import { useCanvas } from "./hooks/use-canvas";

interface Transition {
  target_agent_id: string;
  label: string;
  condition: string;
  signal: string;
}

interface WorkflowAgent {
  sub_agents_id: string;
  name: string;
  description: string;
  system_prompt: string;
  x: number;
  y: number;
  transitions: Transition[];
  handles: string[];
  does_not_handle: string[];
  toolNames: string[];
}

interface WorkflowTool {
  function_tool_id?: string;
  name?: string;
  actual_name?: string;
  description?: string;
  agent_ids?: string[];
}

interface WorkflowConfig {
  router?: {
    system_prompt?: string;
    confidence_threshold?: number;
    fallback_agent_id?: string;
  };
  global_rules?: {
    always_in_context?: string;
    description?: string;
    max_turns_per_agent?: number;
    on_max_turns_exceeded?: string;
  };
  conversation_flow?: {
    opening_agent_id?: string;
    closing_agent_id?: string;
  };
  initial_message?: { text?: string };
  stt_language?: string;
  language_switch?: boolean;
  sentence_catalogue?: StudioSentence[];
  agents?: WorkflowAgent[];
  tools?: WorkflowTool[];
  [key: string]: unknown;
}

interface WorkflowDocument {
  id: string;
  agent_id?: string;
  agent_name?: string;
  version_id?: string;
  version_name?: string;
  description?: string;
  status?: "draft" | "published" | "archived";
  folder_id?: string | null;
  call_infields?: StudioCallField[];
  call_outfields?: StudioCallField[];
  edge_labels?: Record<string, string>;
  config?: WorkflowConfig;
}

interface GlobalDraft {
  name: string;
  description: string;
  versionName: string;
  initialMessage: string;
  globalContext: string;
  routerPrompt: string;
  confidence: number;
  fallbackAgentId: string;
  language: string;
  languageSwitch: boolean;
  maxTurns: number;
  infields: StudioCallField[];
  outcomes: StudioCallField[];
  sentences: StudioSentence[];
}

type Selection =
  | { type: "node"; agentId: string }
  | { type: "edge"; sourceId: string; targetId: string }
  | { type: "global" };

type GlobalSection = "overview" | "router" | "speech" | "data" | "sentences";
type AgentSection = "overview" | "prompt" | "routing";
type PromptTarget =
  | { type: "node"; agentId: string }
  | { type: "router" }
  | { type: "context" }
  | null;

const CANVAS_WIDTH = 3200;
const CANVAS_HEIGHT = 1900;
const NODE_WIDTH = 224;
const NODE_HEIGHT = 124;
const START_X = 72;
const START_Y = 250;
const START_WIDTH = 132;
const START_HEIGHT = 64;

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function signalFromName(name: string) {
  const slug = name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `[TRANSFER_TO_${slug || "AGENT"}]`;
}

function edgeKey(sourceId: string, transition: Transition) {
  return [sourceId, transition.target_agent_id, transition.signal, transition.condition].join("|");
}

function makeAgent(index: number, position?: { x: number; y: number }): WorkflowAgent {
  return {
    sub_agents_id: `sub-${crypto.randomUUID().slice(0, 8)}`,
    name: index === 0 ? "Opening agent" : `Agent ${index + 1}`,
    description: index === 0 ? "Identify the caller's intent and hand off to the correct specialist." : "",
    system_prompt: "",
    x: position?.x ?? 330 + (index % 3) * 320,
    y: position?.y ?? 190 + Math.floor(index / 3) * 210,
    transitions: [],
    handles: [],
    does_not_handle: [],
    toolNames: [],
  };
}

function hydrateAgents(config: WorkflowConfig, labels: Record<string, string>): WorkflowAgent[] {
  const toolsByAgent = new Map<string, string[]>();
  for (const tool of config.tools ?? []) {
    const actualName = tool.actual_name || tool.name;
    if (!actualName) continue;
    for (const agentId of tool.agent_ids ?? []) {
      toolsByAgent.set(agentId, [...(toolsByAgent.get(agentId) ?? []), actualName]);
    }
  }
  return (config.agents ?? []).map((agent, index) => {
    const id = agent.sub_agents_id || `sub-${index + 1}`;
    const transitions = (agent.transitions ?? []).map((transition) => {
      const normalized = {
        target_agent_id: transition.target_agent_id,
        label: transition.label || "",
        condition: transition.condition || "",
        signal: transition.signal || signalFromName(transition.target_agent_id),
      };
      return {
        ...normalized,
        label: normalized.label || labels[edgeKey(id, normalized)] || "",
      };
    });
    return {
      ...agent,
      sub_agents_id: id,
      name: agent.name || `Agent ${index + 1}`,
      description: agent.description || "",
      system_prompt: agent.system_prompt || "",
      x: Number.isFinite(agent.x) ? Number(agent.x) : 330 + (index % 3) * 320,
      y: Number.isFinite(agent.y) ? Number(agent.y) : 190 + Math.floor(index / 3) * 210,
      transitions,
      handles: Array.isArray(agent.handles) ? agent.handles : [],
      does_not_handle: Array.isArray(agent.does_not_handle) ? agent.does_not_handle : [],
      toolNames: toolsByAgent.get(id) ?? [],
    };
  });
}

function buildTools(agents: WorkflowAgent[], options: WorkflowTool[]) {
  const ownership = new Map<string, Set<string>>();
  for (const agent of agents) {
    for (const name of agent.toolNames) {
      if (!ownership.has(name)) ownership.set(name, new Set());
      ownership.get(name)?.add(agent.sub_agents_id);
    }
  }
  return [...ownership.entries()].sort(([left], [right]) => left.localeCompare(right)).map(([name, owners]) => {
    const option = options.find((item) => item.actual_name === name || item.name === name);
    return {
      function_tool_id: option?.function_tool_id || "",
      name: option?.name || name.replace(/_/g, " "),
      actual_name: option?.actual_name || name,
      status: "active",
      agent_ids: [...owners].sort(),
    };
  });
}

function cleanFields(fields: StudioCallField[], infields: boolean) {
  return fields.filter((field) => field.fieldName.trim()).map((field) => ({
    fieldName: field.fieldName.trim(),
    fieldType: field.fieldType || "string",
    visible: field.visible !== false,
    description: field.description || "",
    ...(infields ? {
      normalise: field.normalise === true,
      emphasis: field.emphasis === true,
      to_lowercase: field.to_lowercase === true,
      infield_language: field.infield_language || "NA",
    } : {
      validValues: field.validValues || [],
      defaultValue: (field.defaultValue || "").trim(),
      retryValues: field.retryValues || [],
      ...(field.sections ? { sections: field.sections } : {}),
      ...(field.freeText ? { freeText: true } : {}),
      ...(field.acceptedFindings?.length ? { acceptedFindings: field.acceptedFindings } : {}),
    }),
  }));
}

function PromptEditor({
  value,
  onChange,
  options,
  rows = 8,
  onFullscreen,
}: {
  value: string;
  onChange: (value: string) => void;
  options: PromptTokenOption[];
  rows?: number;
  onFullscreen: () => void;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [mention, setMention] = useState<{ cursor: number; selected: number } | null>(null);
  const insert = (option: PromptTokenOption) => {
    if (!mention) return;
    const before = value.slice(0, mention.cursor - 1);
    const after = value.slice(mention.cursor);
    const inserted = option.insertText || `{{${option.label || ""}}}`;
    onChange(`${before}${inserted}${after}`);
    setMention(null);
    requestAnimationFrame(() => {
      const cursor = before.length + inserted.length;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(cursor, cursor);
    });
  };
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onFullscreen}
        title="Open fullscreen editor"
        className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-md border border-line bg-elevated text-ink-3 hover:text-ink"
      >
        <Maximize2 className="h-3.5 w-3.5" />
      </button>
      <textarea
        ref={textareaRef}
        rows={rows}
        value={value}
        onChange={(event) => {
          const cursor = event.target.selectionStart || 0;
          onChange(event.target.value);
          setMention(event.target.value.slice(0, cursor).endsWith("@") ? { cursor, selected: 0 } : null);
        }}
        className="w-full resize-y rounded-md border border-line bg-soft px-3 py-3 pr-11 font-mono text-[11px] leading-5 text-ink outline-none focus:border-brand"
      />
      {mention && (
        <PromptTokenMenu
          options={options}
          selectedIndex={mention.selected}
          onSelect={insert}
          onHover={(selected) => setMention((current) => current ? { ...current, selected } : current)}
          style={{ left: 8, bottom: 8 }}
        />
      )}
    </div>
  );
}

function FullscreenPromptEditor({
  title,
  value,
  onChange,
  onClose,
}: {
  title: string;
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
      <div className="flex h-[min(90vh,920px)] w-full max-w-6xl flex-col overflow-hidden rounded-md border border-line bg-elevated shadow-2xl">
        <div className="flex h-14 items-center justify-between border-b border-line px-5">
          <div><h2 className="text-sm font-bold text-ink">{title}</h2><p className="font-mono text-[10px] text-ink-3">{value.length.toLocaleString()} characters</p></div>
          <button type="button" onClick={onClose} className="rounded-md p-2 text-ink-3 hover:bg-soft hover:text-ink"><X className="h-4 w-4" /></button>
        </div>
        <textarea value={value} onChange={(event) => onChange(event.target.value)} className="min-h-0 flex-1 resize-none border-0 bg-[#fafaf8] p-6 font-mono text-[13px] leading-6 text-ink outline-none" />
      </div>
    </div>
  );
}

export function WorkflowStudio() {
  const navigate = useNavigate();
  const toast = useToast();
  const outcomeGate = useOutcomeGate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const workflowId = searchParams.get("multi_agent_config_id") || "";
  const isNew = searchParams.get("new") === "1" && !workflowId;
  const infieldLanguages = useMemo(
    () => normalizeInfieldLanguageOptions(user?.infield_languages),
    [user?.infield_languages],
  );
  const [document, setDocument] = useState<WorkflowDocument | null>(null);
  const [agents, setAgents] = useState<WorkflowAgent[]>([]);
  const [tools, setTools] = useState<WorkflowTool[]>([]);
  const [openingAgentId, setOpeningAgentId] = useState("");
  const [globalDraft, setGlobalDraft] = useState<GlobalDraft>({
    name: "Untitled Workflow",
    description: "",
    versionName: "Version 1",
    initialMessage: "",
    globalContext: "",
    routerPrompt: "",
    confidence: 0.6,
    fallbackAgentId: "",
    language: "hi",
    languageSwitch: false,
    maxTurns: 999,
    infields: [],
    outcomes: [],
    sentences: [],
  });
  const [selection, setSelection] = useState<Selection>({ type: "global" });
  const [globalSection, setGlobalSection] = useState<GlobalSection>("overview");
  const [agentSection, setAgentSection] = useState<AgentSection>("overview");
  const [mapOpen, setMapOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [connectionSource, setConnectionSource] = useState("");

  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishMode, setPublishMode] = useState<PublishMode>("new_version");
  const [newVersionName, setNewVersionName] = useState("");
  const [overwriteVersionName, setOverwriteVersionName] = useState("");
  const [testOpen, setTestOpen] = useState(false);
  const [promptTarget, setPromptTarget] = useState<PromptTarget>(null);
  
  const {
    camera,
    setCamera,
    canvasTool,
    setCanvasTool,
    canvasRef,
    startNodeDrag,
    startPan,
    zoomAt,
    fitCanvas: fitCanvasHook
  } = useCanvas<WorkflowAgent>({
    canvasWidth: CANVAS_WIDTH,
    canvasHeight: CANVAS_HEIGHT,
    nodeWidth: NODE_WIDTH,
    nodeHeight: NODE_HEIGHT,
    startX: START_X,
    startY: START_Y,
    startWidth: START_WIDTH,
    startHeight: START_HEIGHT,
    onNodesChange: (updater) => setAgents(updater),
    onSelectionChange: (id) => setSelection({ type: "node", agentId: id }),
    onDirty: () => {},
    getId: (agent) => agent.sub_agents_id
  });

  const fitCanvas = () => fitCanvasHook(agents);

  const edgesCanvasRef = useRef<HTMLCanvasElement>(null);
  const baseline = useRef<{ agents: WorkflowAgent[]; global: GlobalDraft; opening: string } | null>(null);
  const dirty = useMemo(() => {
    if (!baseline.current) return false;
    return (
      JSON.stringify(agents) !== JSON.stringify(baseline.current.agents) ||
      JSON.stringify(globalDraft) !== JSON.stringify(baseline.current.global) ||
      openingAgentId !== baseline.current.opening
    );
  }, [agents, globalDraft, openingAgentId]);

  const selectedAgent = selection.type === "node"
    ? agents.find((agent) => agent.sub_agents_id === selection.agentId) || null
    : null;
  const selectedEdge = selection.type === "edge"
    ? agents.find((agent) => agent.sub_agents_id === selection.sourceId)?.transitions.find((transition) => transition.target_agent_id === selection.targetId) || null
    : null;
  const promptOptions = useMemo(
    () => getPromptMentionOptions(globalDraft.infields, globalDraft.sentences) as PromptTokenOption[],
    [globalDraft.infields, globalDraft.sentences],
  );

  const setBaseline = useCallback((nextAgents: WorkflowAgent[], nextGlobal: GlobalDraft, opening: string) => {
    baseline.current = { agents: cloneValue(nextAgents), global: cloneValue(nextGlobal), opening };
  }, []);

  const applyDocument = useCallback((next: WorkflowDocument) => {
    const config = next.config || {};
    const nextAgents = hydrateAgents(config, next.edge_labels || {});
    const opening = config.conversation_flow?.opening_agent_id
      || nextAgents.find((agent) => !nextAgents.some((source) => source.transitions.some((route) => route.target_agent_id === agent.sub_agents_id)))?.sub_agents_id
      || nextAgents[0]?.sub_agents_id
      || "";
    const nextGlobal: GlobalDraft = {
      name: next.agent_name || "Untitled Workflow",
      description: next.description || "",
      versionName: next.version_name || "Version 1",
      initialMessage: config.initial_message?.text || "",
      globalContext: config.global_rules?.always_in_context || "",
      routerPrompt: config.router?.system_prompt || "",
      confidence: Number(config.router?.confidence_threshold ?? 0.6),
      fallbackAgentId: config.router?.fallback_agent_id || opening,
      language: config.stt_language || "hi",
      languageSwitch: config.language_switch === true,
      maxTurns: Number(config.global_rules?.max_turns_per_agent ?? 999),
      infields: Array.isArray(next.call_infields) ? next.call_infields : [],
      outcomes: Array.isArray(next.call_outfields) ? next.call_outfields : [],
      sentences: Array.isArray(config.sentence_catalogue) ? config.sentence_catalogue : [],
    };
    setDocument(next);
    setAgents(nextAgents);
    setOpeningAgentId(opening);
    setGlobalDraft(nextGlobal);
    setSelection(nextAgents[0] ? { type: "node", agentId: nextAgents[0].sub_agents_id } : { type: "global" });
    setBaseline(nextAgents, nextGlobal, opening);
  }, [setBaseline]);

  const load = useCallback(async () => {
    if (isNew) {
      const first = makeAgent(0);
      const initialGlobal: GlobalDraft = {
        name: "Untitled Workflow",
        description: "",
        versionName: "Version 1",
        initialMessage: "",
        globalContext: "",
        routerPrompt: "",
        confidence: 0.6,
        fallbackAgentId: first.sub_agents_id,
        language: "hi",
        languageSwitch: false,
        maxTurns: 999,
        infields: [],
        outcomes: [],
        sentences: [],
      };
      setDocument({ id: "", agent_name: initialGlobal.name, version_name: initialGlobal.versionName, status: "draft", config: {} });
      setAgents([first]);
      setOpeningAgentId(first.sub_agents_id);
      setGlobalDraft(initialGlobal);
      setSelection({ type: "node", agentId: first.sub_agents_id });
      setBaseline([first], initialGlobal, first.sub_agents_id);
      return;
    }
    if (!workflowId) throw new Error("Workflow id is missing.");
    const next = await core<WorkflowDocument>(`/api/v1/multi-agent-prompts/${encodeURIComponent(workflowId)}`);
    if (!(next.config?.agents || []).length) {
      const first = makeAgent(0);
      applyDocument({ ...next, config: { ...(next.config || {}), agents: [first] } });
      return;
    }
    applyDocument(next);
  }, [applyDocument, isNew, setBaseline, workflowId]);

  useEffect(() => {
    let active = true;
    load()
      .catch((error) => active && toast.danger("Could not load workflow", (error as Error).message))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [load, toast]);

  useEffect(() => {
    core<WorkflowTool[]>("/api/v1/agents/workflow-function-tools")
      .then((rows) => setTools(Array.isArray(rows) ? rows : []))
      .catch((error) => toast.danger("Could not load workflow tools", (error as Error).message));
  }, [toast]);

  const markDirty = () => {};
  const inspect = (nextSelection: Selection) => {
    setSelection(nextSelection);
    setInspectorOpen(true);
    setMapOpen(false);
  };
  const updateGlobal = <K extends keyof GlobalDraft>(key: K, value: GlobalDraft[K]) => {
    setGlobalDraft((current) => ({ ...current, [key]: value }));
    markDirty();
  };
  const updateAgent = <K extends keyof WorkflowAgent>(agentId: string, key: K, value: WorkflowAgent[K]) => {
    setAgents((current) => current.map((agent) => agent.sub_agents_id === agentId ? { ...agent, [key]: value } : agent));
    markDirty();
  };

  const addAgent = (sourceId?: string) => {
    const source = agents.find((agent) => agent.sub_agents_id === sourceId);
    const next = makeAgent(agents.length, source
      ? { x: source.x + 320, y: source.y + Math.min(180, source.transitions.length * 130) }
      : undefined);
    setAgents((current) => current.map((agent) => agent.sub_agents_id === sourceId
      ? { ...agent, transitions: [...agent.transitions, { target_agent_id: next.sub_agents_id, label: "", condition: "", signal: signalFromName(next.name) }] }
      : agent).concat(next));
    inspect({ type: "node", agentId: next.sub_agents_id });
    markDirty();
  };

  const removeAgent = (agentId: string) => {
    if (agents.length <= 1) {
      toast.warning("Opening agent required", "A workflow must contain at least one agent.");
      return;
    }
    const next = agents
      .filter((agent) => agent.sub_agents_id !== agentId)
      .map((agent) => ({ ...agent, transitions: agent.transitions.filter((transition) => transition.target_agent_id !== agentId) }));
    setAgents(next);
    if (openingAgentId === agentId) setOpeningAgentId(next[0]?.sub_agents_id || "");
    setSelection(next[0] ? { type: "node", agentId: next[0].sub_agents_id } : { type: "global" });
    markDirty();
  };

  const addConnection = (sourceId: string, targetId: string) => {
    if (!targetId || sourceId === targetId) return;
    if (sourceId === "start") {
      setOpeningAgentId(targetId);
      updateGlobal("fallbackAgentId", targetId);
      setConnectionSource("");
      return;
    }
    const source = agents.find((agent) => agent.sub_agents_id === sourceId);
    if (!source || source.transitions.some((transition) => transition.target_agent_id === targetId)) {
      setConnectionSource("");
      return;
    }
    const target = agents.find((agent) => agent.sub_agents_id === targetId);
    setAgents((current) => current.map((agent) => agent.sub_agents_id === sourceId ? {
      ...agent,
      transitions: [...agent.transitions, {
        target_agent_id: targetId,
        label: "",
        condition: "",
        signal: signalFromName(target?.name || targetId),
      }],
    } : agent));
    inspect({ type: "edge", sourceId, targetId });
    setConnectionSource("");
  };

  const updateEdge = (patch: Partial<Transition>) => {
    if (selection.type !== "edge") return;
    setAgents((current) => current.map((agent) => agent.sub_agents_id === selection.sourceId ? {
      ...agent,
      transitions: agent.transitions.map((transition) => transition.target_agent_id === selection.targetId ? { ...transition, ...patch } : transition),
    } : agent));
  };

  const removeEdge = () => {
    if (selection.type !== "edge") return;
    setAgents((current) => current.map((agent) => agent.sub_agents_id === selection.sourceId ? {
      ...agent,
      transitions: agent.transitions.filter((transition) => transition.target_agent_id !== selection.targetId),
    } : agent));
    setSelection({ type: "node", agentId: selection.sourceId });
    markDirty();
  };

  const toggleTool = (agentId: string, toolName: string) => {
    const agent = agents.find((item) => item.sub_agents_id === agentId);
    if (!agent) return;
    updateAgent(
      agentId,
      "toolNames",
      agent.toolNames.includes(toolName)
        ? agent.toolNames.filter((name) => name !== toolName)
        : [...agent.toolNames, toolName],
    );
  };

  // Canvas functions are now provided by the useCanvas hook

  const discardLocalChanges = useCallback(() => {
    if (!baseline.current) return;
    setAgents(cloneValue(baseline.current.agents));
    setGlobalDraft(cloneValue(baseline.current.global));
    setOpeningAgentId(baseline.current.opening);
    setSelection(baseline.current.agents[0] ? { type: "node", agentId: baseline.current.agents[0].sub_agents_id } : { type: "global" });
    setConnectionSource("");
  }, []);
  const requestDiscard = useDiscardWarning({ dirty, discard: discardLocalChanges });

  const payload = (versionName = globalDraft.versionName) => {
    const terminal = agents.find((agent) => agent.toolNames.includes("end_call"))?.sub_agents_id
      || agents[agents.length - 1]?.sub_agents_id
      || openingAgentId;
    const config: WorkflowConfig = {
      router: {
        system_prompt: globalDraft.routerPrompt,
        confidence_threshold: globalDraft.confidence,
        fallback_agent_id: globalDraft.fallbackAgentId || openingAgentId,
      },
      global_rules: {
        always_in_context: globalDraft.globalContext,
        description: globalDraft.description,
        max_turns_per_agent: globalDraft.maxTurns,
        on_max_turns_exceeded: terminal,
      },
      conversation_flow: {
        opening_agent_id: openingAgentId,
        closing_agent_id: terminal,
      },
      initial_message: { text: globalDraft.initialMessage },
      stt_language: globalDraft.language,
      language_switch: globalDraft.languageSwitch,
      sentence_catalogue: globalDraft.sentences.filter((item) => item.name.trim()),
      agents: agents.map((agent) => ({
        ...agent,
        x: Math.round(agent.x),
        y: Math.round(agent.y),
        is_terminal: agent.toolNames.includes("end_call"),
        handles: agent.handles.filter(Boolean),
        does_not_handle: agent.does_not_handle.filter(Boolean),
        transitions: agent.transitions.map((transition) => ({ ...transition })),
        toolNames: undefined,
      })) as unknown as WorkflowAgent[],
      tools: buildTools(agents, tools),
    };
    const edgeLabels = Object.fromEntries(
      agents.flatMap((agent) => agent.transitions.filter((transition) => transition.label.trim()).map((transition) => [edgeKey(agent.sub_agents_id, transition), transition.label.trim()])),
    );
    return {
      agent_name: globalDraft.name.trim(),
      version_name: versionName.trim() || "Version 1",
      description: globalDraft.description.trim(),
      status: "published",
      edge_labels: edgeLabels,
      call_infields: cleanFields(globalDraft.infields, true),
      call_outfields: cleanFields(globalDraft.outcomes, false),
      sentence_catalogue: globalDraft.sentences,
      config,
    };
  };

  // What the outcome check compares: this draft's outcomes against the workflow's stored ones.
  const outcomeCheckRequest = {
    ...(isNew || !workflowId ? {} : { workflow_id: workflowId }),
    call_outcomes: cleanFields(globalDraft.outcomes, false),
  };
  useEffect(() => {
    if (globalSection !== "data") return undefined;
    const timer = window.setTimeout(() => {
      void outcomeGate.check(outcomeCheckRequest, { advisoryOnly: isNew });
    }, 900);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSection, globalDraft.outcomes, workflowId, isNew]);

  const handlePublishClick = () => {
    const incompleteAgent = agents.find((agent) => !agent.name.trim() || !agent.system_prompt.trim());
    const invalidRoute = agents.flatMap((agent) => agent.transitions.map((transition) => ({ agent, transition })))
      .find(({ transition }) => !transition.condition.trim() || !transition.signal.trim());
    if (!globalDraft.name.trim() || !globalDraft.initialMessage.trim() || incompleteAgent || !openingAgentId) {
      toast.warning("Workflow is incomplete", "Add an opening message and complete every agent prompt.");
      return;
    }
    if (invalidRoute) {
      toast.warning("Branch configuration required", `Complete the route from ${invalidRoute.agent.name}.`);
      inspect({ type: "edge", sourceId: invalidRoute.agent.sub_agents_id, targetId: invalidRoute.transition.target_agent_id });
      return;
    }
    const currentName = globalDraft.versionName.trim() || "Version 1";
    const versionMatch = currentName.match(/^(.*?)(\d+)$/);
    setNewVersionName(
      isNew
        ? currentName
        : versionMatch
          ? `${versionMatch[1]}${Number(versionMatch[2]) + 1}`
          : `${currentName} 2`,
    );
    setOverwriteVersionName(currentName);
    setPublishMode("new_version");
    setPublishDialogOpen(true);
  };

  const publish = async (mode: PublishMode, versionName: string, overrides: OutcomeOverride[] = []) => {
    const resolvedVersionName = versionName.trim();
    if (!resolvedVersionName) {
      toast.warning("Version name required", "Enter a name before publishing.");
      return;
    }
    if (mode === "overwrite" && isNew) {
      toast.warning("Nothing to overwrite", "Publish the first workflow version first.");
      return;
    }
    try {
      setPublishing(true);
      // Outcome check: advice for everyone; in enforce mode it can stop the publish until the author
      // fixes the problem or accepts it with a reason. A brand-new workflow is only advised.
      if (!overrides.length) {
        const stopped = await outcomeGate.stopsPublish(
          { ...outcomeCheckRequest },
          (accepted) => publish(mode, versionName, accepted),
          { advisoryOnly: isNew },
        );
        if (stopped) {
          setPublishDialogOpen(false);
          return;
        }
      }
      const createVersion = isNew || mode === "new_version";
      const existingDocument = document;
      const requestPayload = {
        ...payload(resolvedVersionName),
        ...(overrides.length ? { outcome_overrides: overrides } : {}),
        ...(createVersion && !isNew && existingDocument?.agent_id
          ? {
              agent_id: existingDocument.agent_id,
              folder_id: existingDocument.folder_id || null,
            }
          : {}),
      };
      const next = await core<WorkflowDocument>(
        createVersion
          ? "/api/v1/multi-agent-prompts"
          : `/api/v1/multi-agent-prompts/${encodeURIComponent(workflowId)}`,
        {
          method: createVersion ? "POST" : "PUT",
          body: JSON.stringify(requestPayload),
        },
      );
      applyDocument(next);
      if (!createVersion) void outcomeGate.check(outcomeCheckRequest);
      setPublishDialogOpen(false);
      toast.success(
        mode === "overwrite" ? "Workflow version overwritten" : "Workflow version published",
        resolvedVersionName,
      );
      if (createVersion) {
        navigate(
          `/home/create-workflow?multi_agent_config_id=${encodeURIComponent(next.id)}`,
          { replace: true },
        );
      }
    } catch (error) {
      const stoppedFindings = findingsFromError(error);
      if (stoppedFindings) {
        setPublishDialogOpen(false);
        outcomeGate.openBlock(stoppedFindings, (accepted) => publish(mode, versionName, accepted));
      } else {
        toast.danger("Workflow publish failed", (error as Error).message);
      }
    } finally {
      setPublishing(false);
    }
  };

  const openTest = () => {
    if (isNew || dirty) {
      toast.warning("Publish before testing", dirty ? "Publish or discard local changes first." : "The workflow has not been published.");
      return;
    }
    setTestOpen(true);
  };

  const edgeGeometry = useCallback((source: WorkflowAgent, target: WorkflowAgent) => {
    const x1 = source.x + NODE_WIDTH;
    const y1 = source.y + NODE_HEIGHT / 2;
    const x2 = target.x;
    const y2 = target.y + NODE_HEIGHT / 2;
    const bend = Math.max(70, Math.abs(x2 - x1) * 0.5);
    return { x1, y1, x2, y2, path: `M${x1} ${y1} C${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`, chipX: (x1 + x2) / 2, chipY: (y1 + y2) / 2 };
  }, []);

  useEffect(() => {
    const canvas = edgesCanvasRef.current;
    const host = canvasRef.current;
    if (!canvas || !host) return undefined;

    const drawCurve = (
      context: CanvasRenderingContext2D,
      geometry: { x1: number; y1: number; x2: number; y2: number },
    ) => {
      const bend = Math.max(70, Math.abs(geometry.x2 - geometry.x1) * 0.5);
      context.beginPath();
      context.moveTo(geometry.x1, geometry.y1);
      context.bezierCurveTo(
        geometry.x1 + bend,
        geometry.y1,
        geometry.x2 - bend,
        geometry.y2,
        geometry.x2,
        geometry.y2,
      );
    };

    const drawArrow = (
      context: CanvasRenderingContext2D,
      geometry: { x1: number; x2: number; y2: number },
      color: string,
      size: number,
    ) => {
      context.save();
      context.translate(geometry.x2, geometry.y2);
      context.rotate(geometry.x2 >= geometry.x1 ? 0 : Math.PI);
      context.fillStyle = color;
      context.beginPath();
      context.moveTo(0, 0);
      context.lineTo(-size, -size * 0.55);
      context.lineTo(-size, size * 0.55);
      context.closePath();
      context.fill();
      context.restore();
    };

    const drawConnection = (
      context: CanvasRenderingContext2D,
      geometry: { x1: number; y1: number; x2: number; y2: number },
      color: string,
      selected = false,
    ) => {
      drawCurve(context, geometry);
      context.strokeStyle = "#ffffff";
      context.lineWidth = selected ? 9 : 8;
      context.lineCap = "round";
      context.stroke();

      drawCurve(context, geometry);
      context.strokeStyle = color;
      context.lineWidth = selected ? 4.5 : 3.5;
      context.lineCap = "round";
      context.stroke();
      drawArrow(context, geometry, color, selected ? 13 : 11);
    };

    const paint = () => {
      const rect = host.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;

      const context = canvas.getContext("2d");
      if (!context) return;
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.clearRect(0, 0, rect.width, rect.height);
      context.translate(camera.x, camera.y);
      context.scale(camera.zoom, camera.zoom);

      const openingAgent = agents.find((agent) => agent.sub_agents_id === openingAgentId);
      if (openingAgent) {
        drawConnection(context, {
          x1: START_X + START_WIDTH,
          y1: START_Y + START_HEIGHT / 2,
          x2: openingAgent.x,
          y2: openingAgent.y + NODE_HEIGHT / 2,
        }, "#374151");
      }

      agents.forEach((source) => {
        source.transitions.forEach((transition) => {
          const target = agents.find((agent) => agent.sub_agents_id === transition.target_agent_id);
          if (!target) return;
          const selected = selection.type === "edge"
            && selection.sourceId === source.sub_agents_id
            && selection.targetId === target.sub_agents_id;
          drawConnection(context, edgeGeometry(source, target), selected ? "#f97316" : "#64748b", selected);
        });
      });
    };

    paint();
    const resizeObserver = new ResizeObserver(paint);
    resizeObserver.observe(host);
    return () => resizeObserver.disconnect();
  }, [agents, camera, edgeGeometry, openingAgentId, selection, canvasRef]);

  const promptValue = promptTarget?.type === "node"
    ? agents.find((agent) => agent.sub_agents_id === promptTarget.agentId)?.system_prompt || ""
    : promptTarget?.type === "router"
      ? globalDraft.routerPrompt
      : globalDraft.globalContext;
  const promptTitle = promptTarget?.type === "node"
    ? `${agents.find((agent) => agent.sub_agents_id === promptTarget.agentId)?.name || "Agent"} system prompt`
    : promptTarget?.type === "router" ? "Router instructions" : "Shared workflow context";
  const updatePromptTarget = (value: string) => {
    if (!promptTarget) return;
    if (promptTarget.type === "node") updateAgent(promptTarget.agentId, "system_prompt", value);
    else if (promptTarget.type === "router") updateGlobal("routerPrompt", value);
    else updateGlobal("globalContext", value);
  };

  if (loading) return <div className="flex h-full min-h-0 items-center justify-center bg-canvas"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>;
  if (!document) return <div className="flex h-full min-h-0 items-center justify-center bg-canvas text-sm text-danger">Workflow could not be loaded.</div>;

  const testAgent: Agent = {
    agent_id: document.agent_id || `workflow-${document.id || "unpublished"}`,
    agent_name: globalDraft.name,
    description: globalDraft.description,
    folder_id: document.folder_id ?? null,
    agent_type: "multi",
    multi_agent_config_id: document.id || null,
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-canvas">
      <header className="flex h-16 shrink-0 items-center justify-between border-b border-line bg-elevated px-5">
        <div className="flex min-w-0 items-center gap-3">
          <button type="button" onClick={() => void requestDiscard(() => navigate("/"))} aria-label="Back to agents" className="rounded-md p-2 text-ink-3 hover:bg-soft hover:text-ink"><ArrowLeft className="h-4 w-4" /></button>
          <div className="h-6 w-px bg-line" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <input value={globalDraft.name} onChange={(event) => updateGlobal("name", event.target.value)} maxLength={50} className="max-w-72 border-0 bg-transparent text-sm font-bold text-ink outline-none" aria-label="Workflow name" />
              <span className={cn("rounded px-2 py-0.5 text-[10px] font-bold uppercase", !isNew && document.status === "published" ? "bg-success-soft text-success" : "bg-warning-soft text-warning")}>{isNew ? "Unpublished" : document.status || "draft"}</span>
              {dirty && <span className="text-[10px] font-bold text-warning">Unsaved</span>}
            </div>
            <p className="font-mono text-[10px] text-ink-3">{globalDraft.versionName} / {isNew ? "Not saved" : document.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => inspect({ type: "global" })}
            className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-elevated px-3 text-xs font-bold text-ink hover:bg-soft"
          >
            <Settings2 className="h-3.5 w-3.5" /> Workflow settings
          </button>
          <button type="button" onClick={openTest} disabled={isNew} className="inline-flex h-9 items-center gap-2 rounded-md border border-line bg-elevated px-3 text-xs font-bold text-ink hover:bg-soft disabled:opacity-40"><Phone className="h-3.5 w-3.5" /> Test</button>
          {dirty && <span className="rounded-md bg-warning-soft px-3 py-2 text-[10px] font-bold uppercase text-warning">Local until publish</span>}
          <button type="button" onClick={handlePublishClick} disabled={publishing} className="inline-flex h-9 items-center gap-2 rounded-md bg-brand px-3 text-xs font-bold text-white hover:bg-brand-hover shadow-xs transition-colors disabled:bg-line disabled:text-ink-3">{publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Publish</button>
        </div>
      </header>

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <aside className="absolute bottom-3 left-3 top-3 z-50 flex w-14 flex-col items-center rounded-md border border-line bg-elevated p-2 shadow-lg">
          <button type="button" title="Add agent" onClick={() => addAgent()} className="flex h-10 w-10 items-center justify-center rounded-md bg-ink text-canvas hover:opacity-90"><Plus className="h-4 w-4" /></button>
          <div className="my-2 h-px w-8 bg-line" />
          <button type="button" title="Agent map" onClick={() => setMapOpen((current) => !current)} className={cn("relative flex h-10 w-10 items-center justify-center rounded-md", mapOpen ? "bg-brand-soft text-brand" : "text-ink-3 hover:bg-soft hover:text-ink")}><ListTree className="h-4 w-4" /><span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded bg-ink px-1 font-mono text-[8px] text-canvas">{agents.length}</span></button>
          <button type="button" title="Workflow settings" onClick={() => inspect({ type: "global" })} className={cn("mt-1 flex h-10 w-10 items-center justify-center rounded-md", selection.type === "global" && inspectorOpen ? "bg-brand-soft text-brand" : "text-ink-3 hover:bg-soft hover:text-ink")}><Settings2 className="h-4 w-4" /></button>
          <div className="flex-1" />
          <span className="pb-1 font-mono text-[9px] font-bold text-ink-3">{Math.round(camera.zoom * 100)}%</span>
        </aside>

        {mapOpen && (
          <aside className="absolute bottom-3 left-20 top-3 z-50 flex w-64 flex-col overflow-hidden rounded-md border border-line bg-elevated shadow-xl">
            <div className="flex h-12 items-center justify-between border-b border-line px-4">
              <div><h2 className="text-xs font-bold text-ink">Workflow agents</h2><p className="font-mono text-[9px] text-ink-3">{agents.length} agents / {agents.reduce((count, agent) => count + agent.transitions.length, 0)} branches</p></div>
              <button type="button" title="Close agent map" onClick={() => setMapOpen(false)} className="rounded-md p-2 text-ink-3 hover:bg-soft hover:text-ink"><X className="h-4 w-4" /></button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {agents.map((agent, index) => (
                <button key={agent.sub_agents_id} type="button" onClick={() => inspect({ type: "node", agentId: agent.sub_agents_id })} className={cn("flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left", selection.type === "node" && selection.agentId === agent.sub_agents_id ? "bg-ink text-canvas" : "text-ink-2 hover:bg-soft")}>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-soft font-mono text-[10px] font-bold text-ink">{index + 1}</span>
                  <span className="min-w-0 flex-1"><span className="block truncate text-xs font-bold">{agent.name}</span><span className="block truncate text-[10px] opacity-60">{agent.transitions.length} branches / {agent.toolNames.length} tools</span></span>
                  <ChevronRight className="h-3.5 w-3.5 opacity-40" />
                </button>
              ))}
            </div>
          </aside>
        )}

        <main className="absolute inset-0 overflow-hidden bg-canvas">
          <div className="absolute left-20 top-3 z-40 flex items-center gap-1 rounded-md border border-line bg-elevated p-1 shadow-sm">
            <button type="button" title="Select and move nodes" onClick={() => setCanvasTool("select")} className={cn("flex h-8 w-8 items-center justify-center rounded", canvasTool === "select" ? "bg-ink text-canvas" : "text-ink-3 hover:bg-soft")}><MousePointer2 className="h-4 w-4" /></button>
            <button type="button" title="Pan canvas" onClick={() => setCanvasTool("pan")} className={cn("flex h-8 w-8 items-center justify-center rounded", canvasTool === "pan" ? "bg-ink text-canvas" : "text-ink-3 hover:bg-soft")}><Hand className="h-4 w-4" /></button>
            <span className="mx-1 h-5 w-px bg-line" />
            <button type="button" title="Zoom out" onClick={() => setCamera((current: { x: number; y: number; zoom: number }) => ({ ...current, zoom: Math.max(0.45, current.zoom - 0.1) }))} className="flex h-8 w-8 items-center justify-center rounded text-ink-3 hover:bg-soft"><ZoomOut className="h-4 w-4" /></button>
            <span className="w-10 text-center font-mono text-[10px] font-bold text-ink-2">{Math.round(camera.zoom * 100)}%</span>
            <button type="button" title="Zoom in" onClick={() => setCamera((current: { x: number; y: number; zoom: number }) => ({ ...current, zoom: Math.min(1.7, current.zoom + 0.1) }))} className="flex h-8 w-8 items-center justify-center rounded text-ink-3 hover:bg-soft"><ZoomIn className="h-4 w-4" /></button>
            <button type="button" title="Fit workflow" onClick={fitCanvas} className="flex h-8 w-8 items-center justify-center rounded text-ink-3 hover:bg-soft"><RotateCcw className="h-4 w-4" /></button>
          </div>
          <div className="absolute right-3 top-3 z-40 rounded-md border border-line bg-elevated px-3 py-2 font-mono text-[10px] text-ink-3 shadow-sm">
            {connectionSource ? "Select a destination agent" : `${agents.length} agents / ${agents.reduce((count, agent) => count + agent.transitions.length, 0)} branches`}
          </div>
          <div
            ref={canvasRef}
            onPointerDown={startPan}
            onWheel={zoomAt}
            className={cn("absolute inset-0 overflow-hidden bg-[radial-gradient(#cfcfca_0.5px,transparent_0.5px)] dark:bg-[radial-gradient(#2a2b31_0.5px,transparent_0.5px)] bg-[size:22px_22px]", canvasTool === "pan" ? "cursor-grab active:cursor-grabbing" : "cursor-default")}
          >
            <canvas ref={edgesCanvasRef} data-testid="workflow-connections-canvas" className="pointer-events-none absolute inset-0 z-10 h-full w-full" />
            <div className="absolute left-0 top-0 z-20" style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT, transform: `translate(${camera.x}px,${camera.y}px) scale(${camera.zoom})`, transformOrigin: "0 0" }}>
              <button type="button" onClick={() => connectionSource ? setConnectionSource("") : inspect({ type: "global" })} className="absolute z-20 flex items-center gap-3 rounded-md border border-ink bg-ink px-4 text-left text-canvas shadow-md" style={{ left: START_X, top: START_Y, width: START_WIDTH, height: START_HEIGHT }}>
                <GitBranch className="h-4 w-4" /><span><span className="block text-xs font-bold">Start</span><span className="text-[10px] opacity-60">Incoming call</span></span>
                <span onClick={(event) => { event.stopPropagation(); setConnectionSource(connectionSource === "start" ? "" : "start"); }} className={cn("absolute -right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border-2 border-canvas shadow", connectionSource === "start" ? "bg-brand text-white" : "bg-elevated text-ink")}><Plus className="h-3.5 w-3.5" /></span>
              </button>
              {agents.flatMap((source) => source.transitions.map((transition, index) => {
                const target = agents.find((agent) => agent.sub_agents_id === transition.target_agent_id);
                if (!target) return null;
                const geometry = edgeGeometry(source, target);
                return (
                  <button
                    key={`chip-${source.sub_agents_id}-${target.sub_agents_id}`}
                    type="button"
                    onClick={() => inspect({ type: "edge", sourceId: source.sub_agents_id, targetId: target.sub_agents_id })}
                    className={cn("absolute z-30 max-w-44 -translate-x-1/2 -translate-y-1/2 truncate rounded-md border px-2.5 py-1 text-[10px] font-bold shadow-sm", selection.type === "edge" && selection.sourceId === source.sub_agents_id && selection.targetId === target.sub_agents_id ? "border-ink bg-ink text-canvas" : "border-line bg-elevated text-ink-2 hover:border-ink-3")}
                    style={{ left: geometry.chipX, top: geometry.chipY + index * 18 }}
                  >
                    {transition.label || transition.condition || "Configure branch"}
                  </button>
                );
              }))}
              {agents.map((agent) => {
                const selected = selection.type === "node" && selection.agentId === agent.sub_agents_id;
                const terminal = agent.toolNames.includes("end_call");
                return (
                  <button
                    key={agent.sub_agents_id}
                    type="button"
                    onPointerDown={(event) => startNodeDrag(event, agent)}
                    onClick={() => {
                      if (connectionSource) addConnection(connectionSource, agent.sub_agents_id);
                      else inspect({ type: "node", agentId: agent.sub_agents_id });
                    }}
                    className={cn("absolute z-20 flex flex-col rounded-md border bg-soft text-left shadow-sm hover:shadow-md", selected ? "border-ink ring-2 ring-ink/10" : connectionSource ? "border-brand/50 hover:border-brand" : "border-line")}
                    style={{ left: agent.x, top: agent.y, width: NODE_WIDTH, height: NODE_HEIGHT }}
                  >
                    <span className="flex h-10 items-center gap-2 border-b border-line px-3"><Bot className="h-4 w-4 text-brand" /><span className="min-w-0 flex-1 truncate text-xs font-bold text-ink">{agent.name}</span>{terminal && <span className="rounded bg-danger-soft px-1.5 py-0.5 text-[9px] font-bold uppercase text-danger">End</span>}</span>
                    <span className="line-clamp-2 min-h-0 flex-1 px-3 py-2 text-[11px] leading-4 text-ink-2">{agent.description || "No responsibility defined."}</span>
                    <span className="flex h-7 items-center justify-between bg-soft px-3 font-mono text-[9px] text-ink-3"><span>{agent.toolNames.length} tools</span><span>{agent.transitions.length} branches</span></span>
                    <span onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); setConnectionSource(connectionSource === agent.sub_agents_id ? "" : agent.sub_agents_id); }} className={cn("absolute -right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white shadow", connectionSource === agent.sub_agents_id ? "bg-brand text-white" : "bg-elevated text-ink")}><Plus className="h-3.5 w-3.5" /></span>
                  </button>
                );
              })}
            </div>
          </div>
        </main>

        {inspectorOpen && (
        <aside className={cn("absolute bottom-3 right-3 top-3 z-50 min-h-0 overflow-y-auto rounded-md border border-line bg-elevated shadow-xl", selection.type === "global" ? "w-[min(520px,calc(100%-96px))]" : "w-[min(400px,calc(100%-96px))]")}>
          {selection.type === "node" && selectedAgent && (
            <div>
              <div className="flex h-12 items-center justify-between border-b border-line px-4"><div className="flex items-center gap-2"><Bot className="h-4 w-4 text-brand" /><h2 className="text-xs font-bold text-ink">Agent configuration</h2></div><div className="flex items-center gap-1"><button type="button" title="Delete agent" onClick={() => removeAgent(selectedAgent.sub_agents_id)} className="rounded-md p-2 text-ink-3 hover:bg-danger-soft hover:text-danger"><Trash2 className="h-4 w-4" /></button><button type="button" title="Close inspector" onClick={() => setInspectorOpen(false)} className="rounded-md p-2 text-ink-3 hover:bg-soft hover:text-ink"><X className="h-4 w-4" /></button></div></div>
              <div className="grid grid-cols-3 border-b border-line p-1">
                {([
                  ["overview", Bot, "Overview"],
                  ["prompt", MessageSquareText, "Prompt"],
                  ["routing", GitBranch, "Routing"],
                ] as const).map(([id, Icon, label]) => (
                  <button key={id} type="button" onClick={() => setAgentSection(id)} className={cn("flex h-9 items-center justify-center gap-1.5 rounded-md text-[10px] font-bold", agentSection === id ? "bg-ink text-canvas" : "text-ink-3 hover:bg-soft hover:text-ink")}><Icon className="h-3.5 w-3.5" />{label}</button>
                ))}
              </div>
              <div className="space-y-5 p-4">
                {agentSection === "overview" && <>
                  <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Name</span><input value={selectedAgent.name} onChange={(event) => updateAgent(selectedAgent.sub_agents_id, "name", event.target.value)} className="h-9 w-full rounded-md border border-line bg-soft px-3 text-xs outline-none focus:border-brand" /></label>
                  <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Responsibility</span><textarea rows={4} value={selectedAgent.description} onChange={(event) => updateAgent(selectedAgent.sub_agents_id, "description", event.target.value)} className="w-full resize-y rounded-md border border-line bg-soft px-3 py-2 text-xs leading-5 outline-none focus:border-brand" /></label>
                  <div className="grid grid-cols-2 gap-3">
                    <label><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Handles</span><textarea rows={7} value={selectedAgent.handles.join("\n")} onChange={(event) => updateAgent(selectedAgent.sub_agents_id, "handles", event.target.value.split("\n").map((item) => item.trim()).filter(Boolean))} className="w-full resize-y rounded-md border border-line bg-soft px-2.5 py-2 text-[11px] leading-5 outline-none" /></label>
                    <label><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Does not handle</span><textarea rows={7} value={selectedAgent.does_not_handle.join("\n")} onChange={(event) => updateAgent(selectedAgent.sub_agents_id, "does_not_handle", event.target.value.split("\n").map((item) => item.trim()).filter(Boolean))} className="w-full resize-y rounded-md border border-line bg-soft px-2.5 py-2 text-[11px] leading-5 outline-none" /></label>
                  </div>
                </>}
                {agentSection === "prompt" && <div><div className="mb-1.5 flex items-center justify-between"><span className="font-mono text-[10px] font-bold uppercase text-ink-3">System prompt</span><span className="font-mono text-[9px] text-ink-3">{selectedAgent.system_prompt.length} chars</span></div><PromptEditor rows={22} value={selectedAgent.system_prompt} onChange={(value) => updateAgent(selectedAgent.sub_agents_id, "system_prompt", value)} options={promptOptions} onFullscreen={() => setPromptTarget({ type: "node", agentId: selectedAgent.sub_agents_id })} /></div>}
                {agentSection === "routing" && <>
                  <section><div className="mb-2 flex items-center justify-between"><span className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase text-ink-3"><GitBranch className="h-3.5 w-3.5" /> Branches</span><button type="button" onClick={() => addAgent(selectedAgent.sub_agents_id)} className="inline-flex items-center gap-1 text-[10px] font-bold text-brand"><Plus className="h-3 w-3" /> Agent</button></div><div className="space-y-2">{selectedAgent.transitions.map((transition) => <button key={transition.target_agent_id} type="button" onClick={() => inspect({ type: "edge", sourceId: selectedAgent.sub_agents_id, targetId: transition.target_agent_id })} className="flex w-full items-center gap-2 rounded-md border border-line bg-soft px-3 py-2 text-left text-xs text-ink hover:bg-elevated"><span className="min-w-0 flex-1 truncate">{transition.label || agents.find((agent) => agent.sub_agents_id === transition.target_agent_id)?.name}</span><ChevronRight className="h-3.5 w-3.5 text-ink-3" /></button>)}</div></section>
                  <section><div className="mb-2 flex items-center gap-2 font-mono text-[10px] font-bold uppercase text-ink-3"><Wrench className="h-3.5 w-3.5" /> Tools</div><div className="grid grid-cols-2 gap-2">{tools.map((tool) => { const name = tool.actual_name || tool.name || ""; const active = selectedAgent.toolNames.includes(name); return <button key={name} type="button" onClick={() => toggleTool(selectedAgent.sub_agents_id, name)} className={cn("min-h-11 rounded-md border px-2 text-left text-[10px] font-bold", active ? "border-ink bg-ink text-canvas" : "border-line bg-soft text-ink-2 hover:bg-elevated")}>{active && <Check className="mr-1 inline h-3 w-3" />}{tool.name || name}</button>; })}</div></section>
                </>}
              </div>
            </div>
          )}

          {selection.type === "edge" && selectedEdge && (
            <div>
              <div className="flex h-12 items-center justify-between border-b border-line px-4"><div className="flex items-center gap-2"><GitBranch className="h-4 w-4 text-brand" /><h2 className="text-xs font-bold text-ink">Branch configuration</h2></div><div className="flex items-center gap-1"><button type="button" title="Delete branch" onClick={removeEdge} className="rounded-md p-2 text-ink-3 hover:bg-danger-soft hover:text-danger"><Trash2 className="h-4 w-4" /></button><button type="button" title="Close inspector" onClick={() => setInspectorOpen(false)} className="rounded-md p-2 text-ink-3 hover:bg-soft hover:text-ink"><X className="h-4 w-4" /></button></div></div>
              <div className="space-y-4 p-4">
                <div className="rounded-md border border-line bg-soft p-3 text-xs"><span className="font-bold text-ink">{agents.find((agent) => agent.sub_agents_id === selection.sourceId)?.name}</span><span className="mx-2 text-ink-3">to</span><span className="font-bold text-ink">{agents.find((agent) => agent.sub_agents_id === selection.targetId)?.name}</span></div>
                <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Canvas label</span><input value={selectedEdge.label} onChange={(event) => updateEdge({ label: event.target.value })} placeholder="Qualified lead" className="h-9 w-full rounded-md border border-line bg-soft px-3 text-xs outline-none focus:border-brand" /></label>
                <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Routing condition</span><textarea rows={5} value={selectedEdge.condition} onChange={(event) => updateEdge({ condition: event.target.value })} placeholder="Transfer when the caller confirms they want pricing details." className="w-full resize-y rounded-md border border-line bg-soft px-3 py-2 text-xs leading-5 outline-none focus:border-brand" /></label>
                <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Transfer signal</span><input value={selectedEdge.signal} onChange={(event) => updateEdge({ signal: event.target.value })} className="h-9 w-full rounded-md border border-line bg-soft px-3 font-mono text-[10px] outline-none focus:border-brand" /></label>
                <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Destination</span><select value={selectedEdge.target_agent_id} onChange={(event) => { const nextTarget = event.target.value; if (selection.type === "edge") { updateEdge({ target_agent_id: nextTarget, signal: signalFromName(agents.find((agent) => agent.sub_agents_id === nextTarget)?.name || nextTarget) }); setSelection({ ...selection, targetId: nextTarget }); } }} className="h-9 w-full rounded-md border border-line bg-soft px-3 text-xs outline-none">{agents.filter((agent) => agent.sub_agents_id !== selection.sourceId).map((agent) => <option key={agent.sub_agents_id} value={agent.sub_agents_id}>{agent.name}</option>)}</select></label>
              </div>
            </div>
          )}

          {selection.type === "global" && (
            <div>
              <div className="flex h-12 items-center justify-between border-b border-line px-4"><div className="flex items-center gap-2"><Settings2 className="h-4 w-4 text-brand" /><div><h2 className="text-xs font-bold text-ink">Workflow settings</h2><p className="font-mono text-[9px] text-ink-3">Shared behavior across every agent</p></div></div><button type="button" title="Close settings" onClick={() => setInspectorOpen(false)} className="rounded-md p-2 text-ink-3 hover:bg-soft hover:text-ink"><X className="h-4 w-4" /></button></div>
              <div className="grid grid-cols-5 gap-1 border-b border-line p-1">
                {([
                  ["overview", Settings2, "General"],
                  ["router", GitBranch, "Router"],
                  ["speech", Languages, "Speech"],
                  ["data", Braces, "Data"],
                  ["sentences", MessageSquareText, "Library"],
                ] as const).map(([id, Icon, label]) => <button key={id} type="button" onClick={() => setGlobalSection(id)} className={cn("flex h-10 min-w-0 items-center justify-center gap-1 rounded-md px-1 text-[9px] font-bold", globalSection === id ? "bg-ink text-canvas" : "text-ink-3 hover:bg-soft hover:text-ink")}><Icon className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{label}</span></button>)}
              </div>
              <div className="space-y-5 p-4">
                {globalSection === "overview" && <>
                  <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Description</span><textarea rows={3} value={globalDraft.description} onChange={(event) => updateGlobal("description", event.target.value)} className="w-full resize-y rounded-md border border-line bg-soft px-3 py-2 text-xs outline-none" /></label>
                  <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Opening message</span><textarea rows={3} value={globalDraft.initialMessage} onChange={(event) => updateGlobal("initialMessage", event.target.value)} className="w-full resize-y rounded-md border border-line bg-soft px-3 py-2 text-xs leading-5 outline-none" /></label>
                  <div><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Shared context</span><PromptEditor rows={7} value={globalDraft.globalContext} onChange={(value) => updateGlobal("globalContext", value)} options={promptOptions} onFullscreen={() => setPromptTarget({ type: "context" })} /></div>
                </>}
                {globalSection === "router" && <>
                  <div><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Router instructions</span><PromptEditor rows={9} value={globalDraft.routerPrompt} onChange={(value) => updateGlobal("routerPrompt", value)} options={promptOptions} onFullscreen={() => setPromptTarget({ type: "router" })} /></div>
                  <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Confidence threshold</span><input type="range" min={0} max={1} step={0.05} value={globalDraft.confidence} onChange={(event) => updateGlobal("confidence", Number(event.target.value))} className="w-full accent-black" /><span className="mt-1 block text-right font-mono text-[10px] text-ink-3">{globalDraft.confidence.toFixed(2)}</span></label>
                  <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Fallback agent</span><select value={globalDraft.fallbackAgentId || openingAgentId} onChange={(event) => updateGlobal("fallbackAgentId", event.target.value)} className="h-9 w-full rounded-md border border-line bg-soft px-3 text-xs">{agents.map((agent) => <option key={agent.sub_agents_id} value={agent.sub_agents_id}>{agent.name}</option>)}</select></label>
                </>}
                {globalSection === "speech" && <>
                  <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Transcription language</span><select value={globalDraft.language} onChange={(event) => updateGlobal("language", event.target.value)} className="h-10 w-full rounded-md border border-line bg-soft px-3 text-xs">{STT_LANGUAGE_GROUPS.map((group) => <optgroup key={group.label} label={group.label}>{group.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</optgroup>)}</select></label>
                  <label className="flex items-center justify-between rounded-md border border-line bg-soft px-3 py-3 text-xs font-bold text-ink">Mid-call language switching<input type="checkbox" checked={globalDraft.languageSwitch} onChange={(event) => updateGlobal("languageSwitch", event.target.checked)} /></label>
                  <label className="block"><span className="mb-1.5 block font-mono text-[10px] font-bold uppercase text-ink-3">Maximum turns per agent</span><input type="number" min={1} value={globalDraft.maxTurns} onChange={(event) => updateGlobal("maxTurns", Math.max(1, Number(event.target.value) || 1))} className="h-9 w-full rounded-md border border-line bg-soft px-3 text-xs" /></label>
                </>}
                {globalSection === "data" && <div className="space-y-6"><PostCallFieldEditor label="Call infields" items={globalDraft.infields} onChange={(items) => updateGlobal("infields", items)} infieldLanguages={infieldLanguages} maxDescriptionLength={1000} /><div className="grid gap-3 border-t border-line pt-5"><OutcomeFindingsPanel validation={outcomeGate.validation} showOld={outcomeGate.showOld} onShowOldChange={outcomeGate.setShowOld} reviewing={outcomeGate.reviewing} onReviewWithAi={() => void outcomeGate.reviewWithAi(outcomeCheckRequest, { advisoryOnly: isNew })} /><PostCallFieldEditor findings={outcomeGate.validation?.mode === "off" ? [] : outcomeGate.validation?.findings} showOldFindings={outcomeGate.showOld} label="Call outcomes" items={globalDraft.outcomes} onChange={(items) => updateGlobal("outcomes", items)} maxDescriptionLength={4000} /></div></div>}
                {globalSection === "sentences" && <SentenceLibrary items={globalDraft.sentences} fields={globalDraft.infields} onChange={(items) => updateGlobal("sentences", items)} />}
              </div>
            </div>
          )}
        </aside>
        )}
      </div>

      {outcomeGate.modal}
      {publishDialogOpen && (
        <PublishVersionModal
          entityLabel="workflow"
          mode={publishMode}
          onModeChange={setPublishMode}
          newVersionName={newVersionName}
          onNewVersionNameChange={setNewVersionName}
          overwriteVersionName={overwriteVersionName}
          onOverwriteVersionNameChange={setOverwriteVersionName}
          canOverwrite={!isNew}
          overwriteDisabledReason="Available after the first workflow version has been published."
          publishing={publishing}
          onClose={() => setPublishDialogOpen(false)}
          onPublish={(mode, versionName) => void publish(mode, versionName)}
        />
      )}
      {promptTarget && <FullscreenPromptEditor title={promptTitle} value={promptValue} onChange={updatePromptTarget} onClose={() => setPromptTarget(null)} />}
      {testOpen && <TestCallModal agent={testAgent} onClose={() => setTestOpen(false)} />}
    </div>
  );
}
